import { useEffect, useState } from 'react';

import { HandoutCompanyValues } from './HandoutCompanyValues';
import { HandoutCompetencies } from './HandoutCompetencies';
import { HandoutCompetencyGroups } from './HandoutCompetencyGroups';
import { HandoutEmployeeInfo } from './HandoutEmployeeInfo';
import { HandoutFooter } from './HandoutFooter';
import { HandoutHeader } from './HandoutHeader';
import { HandoutPerformanceScore } from './HandoutPerformanceScore';
import { HandoutPredefinedSections } from './HandoutPredefinedSections';
import { useHandoutEvaluationPeriod } from './useHandoutEvaluationPeriod';

import type { EvaluationPeriodOption } from '../../Evaluation/types';
import type {
    HandoutPredefinedSectionData,
    HandoutPreviewData,
    CompetencyGroup,
} from '@/types/review/ReviewDiscussion';

interface HandoutDocumentProps {
    handoutData: HandoutPreviewData;
    /** The cycle whose Mastered/Growth competency lists are shown/edited. */
    cycleInstanceId: string;
    currentPage?: number;
    totalPages?: number;
    paginatedGroups?: CompetencyGroup[];
    onEvaluationDateSave?: (date: string) => void;
    onEvaluationPeriodSave?: (startDate: string, endDate: string, periodId: string) => void;
    onPerformanceScoreSave?: (score: string) => void;
    employeeName?: string;
    /** The employee being evaluated; used to exclude them from manager search options. */
    employeeId?: string;
    evaluationDate?: string;
    onPredefinedSectionSave?: (sections: HandoutPredefinedSectionData[]) => void;
    onRecommendationsGenerate?: (feeling: string, priority: string) => Promise<string | null>;
    isPredefinedSectionSaving?: boolean;
    disabled?: boolean;
    /** Persisted manager override for the handout header. */
    onManagerNameSave?: (name: string) => void;
    /** Available evaluation periods for the modal */
    evaluationPeriods?: EvaluationPeriodOption[];
}

const stripProfilePrefix = (value?: string) => {
    if (!value) {
        return '';
    }

    return value.replace(/^competency profile:\s*/i, '').trim();
};

export const HandoutDocument = ({
    handoutData,
    cycleInstanceId,
    currentPage = 1,
    totalPages = 4,
    paginatedGroups,
    onEvaluationDateSave,
    onEvaluationPeriodSave,
    onPerformanceScoreSave,
    employeeName,
    employeeId,
    evaluationDate: propEvaluationDate,
    onPredefinedSectionSave,
    onRecommendationsGenerate,
    isPredefinedSectionSaving,
    disabled = false,
    onManagerNameSave,
    evaluationPeriods = [],
}: HandoutDocumentProps) => {
    const [evaluationDate, setEvaluationDate] = useState(
        propEvaluationDate ?? handoutData.evaluationDate ?? '',
    );
    const [evaluationStartDate, setEvaluationStartDate] = useState(
        handoutData.evaluationStartDate ?? '',
    );
    const [evaluationEndDate, setEvaluationEndDate] = useState(handoutData.evaluationEndDate ?? '');
    const [managerName, setManagerName] = useState(handoutData.managerName ?? '');
    const [generatedDate] = useState(() => new Date().toISOString());

    // Use the period management hook
    const { selectedPeriodId } = useHandoutEvaluationPeriod(handoutData);

    const rawEmployeeRole = handoutData.employeeRole ?? '';
    const rawEmployeeProfile = handoutData.employeeProfile ?? rawEmployeeRole;
    const employeeRole = stripProfilePrefix(rawEmployeeRole) || rawEmployeeRole;
    const employeeProfile = stripProfilePrefix(rawEmployeeProfile) || rawEmployeeProfile;
    const isSummaryPage = currentPage === 1;

    useEffect(() => {
        setEvaluationDate(handoutData.evaluationDate ?? '');
        setEvaluationStartDate(handoutData.evaluationStartDate ?? '');
        setEvaluationEndDate(handoutData.evaluationEndDate ?? '');
        setManagerName(handoutData.managerName ?? '');
    }, [
        handoutData.evaluationDate,
        handoutData.evaluationStartDate,
        handoutData.evaluationEndDate,
        handoutData.managerName,
    ]);

    return (
        <div
            className={`rounded-b-sm bg-white shadow-xl ${disabled ? 'pointer-events-none opacity-70 select-none' : ''}`}
            aria-disabled={disabled}
            data-handout-ref={'true'}
            data-handout-total-pages={totalPages}
            data-handout-current-page={currentPage}
            data-handout-employee-name={employeeName || handoutData.employeeName}
            data-handout-evaluation-date={propEvaluationDate || evaluationDate}
        >
            <HandoutHeader
                evaluationDate={evaluationDate}
                evaluationStartDate={evaluationStartDate}
                evaluationEndDate={evaluationEndDate}
                managerName={managerName}
                currentPage={currentPage}
                totalPages={totalPages}
                employeeId={employeeId}
                evaluationPeriods={evaluationPeriods}
                selectedPeriodId={selectedPeriodId}
                onEvaluationDateChange={(v) => {
                    if (disabled) {
                        return;
                    }
                    setEvaluationDate(v);
                    onEvaluationDateSave?.(v);
                }}
                onEvaluationPeriodSave={(startDate, endDate, periodId) => {
                    if (disabled) {
                        return;
                    }
                    onEvaluationPeriodSave?.(startDate, endDate, periodId);
                }}
                onManagerNameChange={(v) => {
                    if (disabled) {
                        return;
                    }
                    setManagerName(v);
                }}
                onManagerNameSave={disabled ? undefined : onManagerNameSave}
            />
            <div className={'relative max-md:p-[16px] md:max-lg:p-[24px] lg:p-[32px]'}>
                {isSummaryPage ? (
                    <>
                        <div>
                            <HandoutEmployeeInfo
                                employeeName={handoutData.employeeName}
                                employeeRole={employeeRole}
                                employeeProfile={employeeProfile}
                                employeeAvatarUrl={handoutData.employeeAvatarUrl}
                            />
                        </div>
                        {/* {renderNoteSection('header')} */}
                        <div>
                            <HandoutPerformanceScore
                                overallPerformanceLevelId={handoutData.overallPerformanceLevelId}
                                overallPerformanceLevel={handoutData.overallPerformanceLevel}
                                disabled={disabled}
                                onChange={(v) => {
                                    if (disabled) {
                                        return;
                                    }
                                    onPerformanceScoreSave?.(v);
                                }}
                            />
                        </div>
                        {/* {renderNoteSection('performance')} */}
                        <div className={'py-4 sm:py-10'}>
                            <HandoutCompanyValues
                                companyValues={handoutData.companyValues}
                                reviewInstanceId={handoutData.reviewInstanceId}
                                proficiencyLevels={handoutData.proficiencyLevels}
                            />
                        </div>
                        {/* {renderNoteSection('values')} */}
                        <div>
                            <HandoutCompetencies
                                cycleInstanceId={cycleInstanceId}
                                competencyGroups={handoutData.competencyGroups}
                                proficiencyLevels={handoutData.proficiencyLevels}
                            />
                        </div>
                        {/* {renderNoteSection('competencies')} */}
                        {/* {renderNoteSection('custom')} */}
                    </>
                ) : (
                    <HandoutCompetencyGroups
                        competencyGroups={handoutData.competencyGroups}
                        proficiencyLevels={handoutData.proficiencyLevels}
                        paginatedGroups={paginatedGroups ?? []}
                        currentPage={currentPage}
                        totalPages={totalPages}
                    />
                )}
                <HandoutPredefinedSections
                    predefinedSection={handoutData.predefinedSection}
                    predefinedSectionKey={handoutData.reviewInstanceId}
                    onPredefinedSectionSave={disabled ? undefined : onPredefinedSectionSave}
                    onRecommendationsGenerate={disabled ? undefined : onRecommendationsGenerate}
                    isSaving={isPredefinedSectionSaving}
                    isSummaryPage={isSummaryPage}
                />
            </div>

            <HandoutFooter
                generatedDate={generatedDate}
                isSummaryPage={isSummaryPage}
            />
        </div>
    );
};
